import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/modules/app';
import JobDto from '@/models/Jobs/JobDto';
import NoteDto from '@/models/Notes/NoteDto';
import UpdateNoteRequest from '@/models/Notes/UpdateNoteRequest';
import CreateNoteRequest from '@/models/Notes/CreateNoteRequest';
import UpdateJobGeneralDetailsRequest from '@/models/Jobs/UpdateJobGeneralDetailsRequest';
import UpdateJobRequirementsRequest from '@/models/Jobs/UpdateJobRequirementsRequest';
import UpdateRichTextRequest from '@/models/Shared/UpdateRichTextRequest';
import UpdateRichTextResponse from '@/models/Shared/UpdateRichTextResponse';
import UpdateJobLocationDetailsRequest from '@/models/Jobs/UpdateJobLocationDetailsRequest';
import JobBrowseWorkersViewModel from '@/models/JobWorkers/JobBrowseWorkersViewModel';
import WorkerSearchViewModel from '@/models/Workers/WorkerSearchViewModel';
import WorkerProfileSearchRequest from '@/models/Workers/WorkerProfileSearchRequest';
import WorkType from '@/models/Shared/WorkType';
import WorkerProfileDto from '@/models/Workers/WorkerProfileDto';
import SelectableEntity from '@/models/Shared/SelectableEntity';
import DistanceViewModel from '@/models/Shared/DistanceViewModel';
import WorkerSearchOptionsResponse from '@/models/Workers/WorkerSearchOptionsResponse';
import PositionDto from '@/models/Positions/PositionDto';
import WorkerStatus from '@/models/Workers/WorkerStatus';
import SkillDto from '@/models/Skills/SkillDto';
import QualificationDto from '@/models/Qualifications/QualificationDto';
import CreateJobWorkersRequest from '@/models/JobWorkers/SendJobToWorkersRequest';
import JobWorkerDto from '@/models/JobWorkers/JobWorkerDto';
import JobWorkersViewModel from '@/models/JobWorkers/JobWorkersViewModel';
import JobWorkerStatus from '@/models/JobWorkers/JobWorkerStatus';
import JobWorkerTabIndex from '@/models/JobWorkers/JobWorkerTabIndex';
import SendJobWorkersToContactRequest from '@/models/JobWorkers/SendJobWorkersToContactRequest';
import JobWorkerInviteRequest from '@/models/JobWorkers/UpdateJobWorkerStatusWorkerInterestedRequest';
import JobLatestStageStatusDto from '@/models/Jobs/JobLatestStageStatusDto';
import UpdateJobStatusRequest from '@/models/Jobs/UpdateJobStatusRequest';
import CreateJobWorkerInterviewViewModel from '@/models/JobWorkerInterviews/CreateJobWorkerInterviewViewModel';
import CreateJobWorkerInterviewRequest from '@/models/JobWorkerInterviews/CreateJobWorkerInterviewRequest';
import JobWorkerInterviewType from '@/models/JobWorkerInterviews/JobWorkerInterviewType';
import JobWorkerInterviewDto from '@/models/JobWorkerInterviews/JobWorkerInterviewDto';
import InPersonInterviewAddressType from '@/models/JobWorkerInterviews/InPersonInterviewAddressType';
import UpdateJobWorkerStatusWorkerInterestedRequest from '@/models/JobWorkers/UpdateJobWorkerStatusWorkerInterestedRequest';
import UpdateJobWorkerStatusContactInterestedRequest from '@/models/JobWorkers/UpdateJobWorkerStatusContactInterestedRequest';
import GeoPoint from '@/models/Shared/GeoPoint';
import UpdateJobWorkerInterviewDetailsViewModel from '@/models/JobWorkerInterviews/UpdateJobWorkerInterviewDetailsViewModel';
import UpdateJobWorkerInterviewDetailsRequest from '@/models/JobWorkerInterviews/UpdateJobWorkerInterviewDetailsRequest';
import UpdateJobWorkerInterviewFeedbackRequest from '@/models/JobWorkerInterviews/UpdateJobWorkerInterviewFeedbackRequest';
import CompleteJobWorkerInterviewRequest from '@/models/JobWorkerInterviews/CompleteJobWorkerInterviewRequest';
import JobCanidateNotInterestedReason from '@/filters/JobWorkerNotInterestedReason';
import UpdateJobWorkerStatusInterviewingRequest from '@/models/JobWorkers/UpdateJobWorkerStatusInterviewingRequest';
import UpdateJobWorkerRejectedRequest from '@/models/JobWorkers/UpdateJobWorkerStatusRejectedRequest';
import CreateJobWorkerOfferRequest from '@/models/JobWorkerOffers/CreateJobWorkerOfferRequest';
import UpdateJobWorkerOfferRequest from '@/models/JobWorkerOffers/UpdateJobWorkerOfferRequest';
import JobWorkerOfferDto from '@/models/JobWorkerOffers/JobWorkerOfferDto';
import UpdateJobWorkerStatusOfferRequest from '@/models/JobWorkers/UpdateJobWorkerStatusOfferRequest';
import UpdateJobWorkerOfferWorkerAcceptanceRequest from '@/models/JobWorkerOffers/UpdateJobWorkerOfferWorkerAcceptanceRequest';
import UpdateJobWorkerOfferContactConfirmationRequest from '@/models/JobWorkerOffers/UpdateJobWorkerOfferContactConfirmationRequest';
import CancelJobWorkerOfferRequest from '@/models/JobWorkerOffers/CancelJobWorkerOfferRequest';
import WorkerDeclineJobWorkerOfferRequest from '@/models/JobWorkerOffers/UpdateJobWorkerOfferContactConfirmationRequest';
import CancelJobWorkerInterviewRequest from '@/models/JobWorkerInterviews/CancelJobWorkerInterviewRequest';
import PlaceJobWorkerOfferRequest from '@/models/JobWorkerOffers/PlaceJobWorkerOfferRequest';
import SendJobWorkerOfferToWorkerRequest from '@/models/JobWorkerOffers/SendJobWorkerOfferToWorkerRequest';
import UpdateJobWorkerPlacementRequest from '@/models/JobWorkerOffers/UpdateJobWorkerPlacementRequest';
import NoteCategoryDto from '@/models/NoteCategories/NoteCategoryDto';

export interface IJobDetailsState {
  loading: boolean;
  loadingWorker: boolean;
  loadingWorkers: boolean;
  loadingDialog: boolean;
  notes: NoteDto[];
  noteCategories: NoteCategoryDto[];
  loadingNotes: boolean;
  currentJob: JobDto | null; 
  currentJobWorkers: JobWorkerDto[];
  browseWorkersVM: JobBrowseWorkersViewModel;
  workerSearchViewModel: WorkerSearchViewModel;
  jobWorkersVM: JobWorkersViewModel;
}

@Module({ dynamic: true, store, name: 'JobDetails', namespaced: true})
class JobDetails extends VuexModule implements IJobDetailsState {
  
  public loading = false;
  public loadingWorker = false; 
  public loadingWorkers = true; 
  public loadingNotes = true;
  public loadingDialog = false;
  public currentJob: JobDto = new JobDto();
  public currentJobWorkers: JobWorkerDto[] = [];
  public browseWorkersVM: JobBrowseWorkersViewModel = new JobBrowseWorkersViewModel();
  public workerSearchViewModel: WorkerSearchViewModel = new WorkerSearchViewModel(null, null, null, false);
  public notes: NoteDto[] = [];
  public noteCategories: NoteCategoryDto[] = [];
  public jobWorkersVM: JobWorkersViewModel = new JobWorkersViewModel();


  @Action
  public async getJob(jobId: string) {
    this.setLoading(true);
    await ajax.get(`/api/v1/agency/jobs/${jobId}`)
    .then(response => {
      this.context.commit("setCurrentJob", response.data);
      this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoading(false);
    });
  }

  @Action
  public async editJobGeneralDetails(request: UpdateJobGeneralDetailsRequest) {
    this.setLoadingDialog(true);
    await ajax.put(`/api/v1/agency/jobs/${this.currentJob.id}/general-details`, request)
    .then(response => {
      this.context.commit("setCurrentJobGeneralDetailsFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  public async editJobLocationDetails(request: UpdateJobLocationDetailsRequest) {
    this.setLoadingDialog(true);
    await ajax.put(`/api/v1/agency/jobs/${this.currentJob.id}/location-details`, request)
    .then(response => {
      this.context.commit("setCurrentJobLocationDetailsFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  public async editJobRequirements(request: UpdateJobRequirementsRequest) {
    this.setLoadingDialog(true);
    await ajax.put(`/api/v1/agency/jobs/${this.currentJob.id}/requirements`, request)
    .then(response => {
      this.context.commit("setCurrentJobRequirementsFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  public async editJobSummary(request: UpdateRichTextRequest) {
    this.setLoadingDialog(true);
    await ajax.put(`/api/v1/agency/jobs/${this.currentJob.id}/summary`, request)
    .then(response => {
      this.context.commit("setCurrentJobSummaryFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  public async editJobDescription(request: UpdateRichTextRequest) {
    this.setLoadingDialog(true);
    await ajax.put(`/api/v1/agency/jobs/${this.currentJob.id}/description`, request)
    .then(response => {
      this.context.commit("setCurrentJobDescriptionFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  public async updateJobStatus(request: UpdateJobStatusRequest) {
    this.setLoadingDialog(true);
    await ajax.put(`/api/v1/agency/jobs/${this.currentJob.id}/status`, request)
    .then(response => {
      this.context.commit("setCurrentJobStatusFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async getNoteCategories() {
    //this.setLoadingNotes(true);
    await ajax.get(`/api/v1/agency/note-categories?entityType=200`)
    .then(response => {
      this.context.commit("setNoteCategories", response.data);
      //this.setLoadingNotes(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      //this.setLoadingNotes(false);
    });
  }

  @Action
  async getNotes() {
    this.setLoadingNotes(true);
    await ajax.get(`/api/v1/agency/jobs/${this.currentJob.id}/notes`)
    .then(response => {
      this.context.commit("setNotes", response.data);
      this.setLoadingNotes(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingNotes(false);
    });
  }

  @Action
  async createNote(request: CreateNoteRequest) {
    this.setLoadingDialog(true);
    await ajax.post(`/api/v1/agency/jobs/${this.currentJob.id}/notes`, request)
    .then(response => {
      this.context.commit("setNoteFromCreate", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async editNote(note: NoteDto) {
    const request = new UpdateNoteRequest();
    request.updatedAt = note.updatedAt
    request.description = note.description;
    this.setLoadingDialog(true);
    await ajax.put(`/api/v1/agency/jobs/${this.currentJob.id}/notes/${note.id}`, request)
    .then(response => {
      this.context.commit("setNoteFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action 
  async resetAdvancedWorkerSearch(){
    this.context.commit("setBrowseJobRequirements");
  }

  @Action 
  async browseDefaultWorkerSearch() {
   
    //Refresh the current job requirements
    this.context.commit("setBrowseJobRequirements");
    
    this.setLoadingWorkers(true);
    const request = new WorkerProfileSearchRequest();
    request.statuses.push(WorkerStatus.Active);
    request.workTypes.push(WorkType.Perm);
    request.currentPositionCode = undefined;

    //Positions
    request.positionCodes = this.workerSearchViewModel.selectedPositions.map(function(position: PositionDto) {
      return position.code;
    });

    //Skills
    request.skillMatchType = this.workerSearchViewModel.selectedSkillMatchType;
    request.skillCodes = this.workerSearchViewModel.selectedSkills.map(function(skill: SkillDto) {
         return skill.code;
    });
 
    //Qualifications
    request.qualificationMatchType = this.workerSearchViewModel.selectedQualificationMatchType;
    request.qualificationCodes = this.workerSearchViewModel.selectedQualifications.map(function(qualification: QualificationDto) {
       return qualification.code;
    });

    request.distance = this.workerSearchViewModel.selectedDistance.value
    if (request.distance != 0) {
      //Fetch lat and long
      request.latitude = JobDetailsModule.currentJob.location.geometry.lat
      request.longitude = JobDetailsModule.currentJob.location.geometry.lng
    }

    //Salary
    if (this.workerSearchViewModel.matchSalary) {
      request.annualSalaryMax = this.currentJob.annualSalaryMax;
    }

    await ajax.post(`/api/v1/agency/workers/search`, request)
    .then(response => {
      if (response.data == null) {
        response.data = [];
      }
      this.context.commit("setBrowseWorkerProfiles", response.data);
      if (response.data.length > 0){
        this.context.commit("setBrowseWorker", response.data[0]);
      }
      this.setLoadingWorkers(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingWorkers(false);
    });
  }

  @Action 
  async browseAdvancedWorkerSearch() {
    
    this.setLoadingWorkers(true);
    const request = new WorkerProfileSearchRequest();

    request.statuses.push(this.workerSearchViewModel.selectedStatus);

    request.workTypes.push(WorkType.Perm);
    request.currentPositionCode = undefined;

    //Positions
    request.positionCodes = this.workerSearchViewModel.selectedPositions.map(function(position: PositionDto) {
      return position.code;
    });

    //Skills
    request.skillMatchType = this.workerSearchViewModel.selectedSkillMatchType;
    request.skillCodes = this.workerSearchViewModel.selectedSkills.map(function(skill: SkillDto) {
         return skill.code;
    });
 
     //Qualifications
     request.qualificationMatchType = this.workerSearchViewModel.selectedQualificationMatchType;
    request.qualificationCodes = this.workerSearchViewModel.selectedQualifications.map(function(qualification: QualificationDto) {
       return qualification.code;
    });

    //Drivers
    if (this.workerSearchViewModel.driversOnly){
      request.driversOnly = true;
      request.minimumPassengers = this.workerSearchViewModel.selectedMinimumPassengers;
    }

    request.distance = this.workerSearchViewModel.selectedDistance.value
    if (request.distance != 0) {
      //Fetch lat and long
      request.latitude = JobDetailsModule.currentJob.location.geometry.lat
      request.longitude = JobDetailsModule.currentJob.location.geometry.lng
    }

    //Salary
    if (this.workerSearchViewModel.matchSalary) {
      request.annualSalaryMax = this.currentJob.annualSalaryMax;
    }
   
    await ajax.post(`/api/v1/agency/workers/search`, request)
    .then(response => {
      if (response.data == null) {
        response.data = [];
      }
      this.context.commit("setBrowseWorkerProfiles", response.data);
      if (response.data.length < 1){
        this.context.commit("setBrowseWorker", response.data);
      }
      this.setLoadingWorkers(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingWorkers(false);
    });
  }

  @Action
  public async getBrowseWorker(id: string) {
    this.setLoadingWorker(true); 
    await ajax.get(`/api/v1/agency/workers/${id}/profile`)
    .then(response => {
      this.context.commit("setBrowseWorker", response.data);
      this.setLoadingWorker(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingWorker(false);
    })
  }

  @Action
  public async updateSelectedBrowseWorker(id: string) {
    const workerProfile = this.browseWorkersVM.workerProfiles.find((findWorker: SelectableEntity<WorkerProfileDto>) => id === findWorker.entity.id);
    if (workerProfile) {
      this.context.commit("setBrowseWorker", workerProfile.entity); 
    } else {
      this.context.commit("setBrowseWorker", null); 
    }
  }

  @Action
  public async addBrowseSelectedWorkerId(workerId: string){
    this.context.commit("setAddBrowseSelectedWorkerId", workerId);
  }

  @Action
  public async removeBrowseSelectedWorkerId(workerId: string){
    this.context.commit("setRemoveBrowseSelectedWorkerId", workerId);
  }

  @Action
  async getWorkerSearchOptions() {
    this.setLoadingDialog(true);
    await ajax.get(`/api/v1/agency/workers/search-options`)
    .then(response => {
      this.context.commit("setWorkerSearchOptions", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action 
  async createJobWorkers(request: CreateJobWorkersRequest) {
    this.setLoadingDialog(true);
    await ajax.post(`/api/v1/agency/jobs/${JobDetailsModule.currentJob.id}/workers`, request)
    .then(response => {
      //TODO something here that triggers a success message
      this.context.commit("clearBrowseSelectedWorkerIds");
      //This doesn't really work because the job workers don't have enough data on them
      //Could fix it but for now just refreshing from the calling component
      //this.context.commit("setJobWorkersFromCreate", response.data.jobWorkers);
      this.context.commit("setLatestStage", response.data.jobLatestStageStatus);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async editJobWorkerInterviewDetails(vm: UpdateJobWorkerInterviewDetailsViewModel) {
    this.setLoadingDialog(true);
    const request = new UpdateJobWorkerInterviewDetailsRequest();
    request.updatedAt = vm.updatedAt;
    request.startAt = new Date(`${vm.date}T${vm.startAtTime}`) 
    const endAt = new Date(`${vm.date}T${vm.endAtTime}`) 
    if (request.startAt > endAt){
      endAt.setDate(endAt.getDate() +1)
    }
    request.endAt = endAt;
    request.contactId = vm.selectedContactLocation.contactId;
    request.type = vm.type;
    if (request.type === JobWorkerInterviewType.InPerson){
      request.inPersonAddressType = vm.inPersonAddressType;
      if (request.inPersonAddressType === InPersonInterviewAddressType.CompanyAddress)
      {
        request.companyAddressId = vm.selectedCompanyAddress.id;
        request.otherLocationId = undefined;
      } else if (request.inPersonAddressType === InPersonInterviewAddressType.OtherLocation) {
        request.otherLocationId = vm.selectedOtherLocation.id;
        request.companyAddressId = undefined;
      } else if (request.inPersonAddressType === InPersonInterviewAddressType.OtherAddress) {
        request.buildingNameNumber = vm.buildingNameNumber;
        request.streetRoadBlock = vm.streetRoadBlock;
        request.locality = vm.locality;
        request.cityTown = vm.cityTown;
        request.stateProvinceRegion = vm.stateProvinceRegion;
        request.postZipCode = vm.postZipCode;
        request.countryCode = vm.country.code;
        request.geometry = new GeoPoint();
        request.geometry.lat = vm.geometry.lat;
        request.geometry.lng = vm.geometry.lng;
        request.isExactGeo = true;
      }
    } else {
      request.inPersonAddressType = InPersonInterviewAddressType.Undefined;
      request.companyAddressId = undefined;
      request.otherLocationId = undefined;
      request.buildingNameNumber = "";
      request.streetRoadBlock = "";
      request.locality = "";
      request.cityTown = "";
      request.stateProvinceRegion = "";
      request.postZipCode = "";
      request.countryCode = "";
      request.geometry = new GeoPoint();
      request.isExactGeo = false;
    }
    
    request.workerInstructions = vm.workerInstructions;
    request.contactInstructions = vm.contactInstructions;

    await ajax.put(`/api/v1/agency/job-worker-interviews/${vm.jobWorkerInterviewId}/details`, request)
    .then(response => {
      this.context.commit("setJobWorkerInterviewFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async editJobWorkerInterviewFeedback(request: UpdateJobWorkerInterviewFeedbackRequest) {
    this.setLoadingDialog(true);
    const jobWorkerInterviewId = request.jobWorkerInterviewId;
    request.jobWorkerInterviewId = undefined;
    await ajax.put(`/api/v1/agency/job-worker-interviews/${jobWorkerInterviewId}/feedback`, request)
    .then(response => {
      this.context.commit("setJobWorkerInterviewFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async confirmJobWorkerInterviewForWorker(jobWorkerInterviewId: string) {
    this.setLoadingDialog(true);
   
    await ajax.put(`/api/v1/agency/job-worker-interviews/${jobWorkerInterviewId}/worker-confirmation`)
    .then(response => {
      this.context.commit("setJobWorkerInterviewFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async confirmJobWorkerInterviewForContact(jobWorkerInterviewId: string) {
    this.setLoadingDialog(true);
   
    await ajax.put(`/api/v1/agency/job-worker-interviews/${jobWorkerInterviewId}/contact-confirmation`)
    .then(response => {
      this.context.commit("setJobWorkerInterviewFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async completeJobWorkerInterview(request: CompleteJobWorkerInterviewRequest) {
    this.setLoadingDialog(true);
    const jobWorkerInterviewId = request.jobWorkerInterviewId;
    request.jobWorkerInterviewId = undefined;
    await ajax.put(`/api/v1/agency/job-worker-interviews/${jobWorkerInterviewId}/status/complete`, request)
    .then(response => {
      this.context.commit("setJobWorkerInterviewFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async cancelJobWorkerInterview(request: CancelJobWorkerInterviewRequest) {
    this.setLoadingDialog(true);
    const jobWorkerInterviewId = request.jobWorkerInterviewId;
    request.jobWorkerInterviewId = undefined;
    await ajax.put(`/api/v1/agency/job-worker-interviews/${jobWorkerInterviewId}/status/cancel`, request)
    .then(response => {
      this.context.commit("setJobWorkerInterviewFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async createJobWorkerInterview(vm: CreateJobWorkerInterviewViewModel) {
    this.setLoadingDialog(true);
    const request = new CreateJobWorkerInterviewRequest();
    request.startAt = new Date(`${vm.date}T${vm.startAtTime}`) 
    const endAt = new Date(`${vm.date}T${vm.endAtTime}`) 
    if (request.startAt > endAt){
      endAt.setDate(endAt.getDate() +1)
    }
    request.endAt = endAt;
    request.contactId = vm.selectedContactLocation.contactId;
    request.type = vm.type;
    if (request.type === JobWorkerInterviewType.InPerson){
      request.inPersonAddressType = vm.inPersonAddressType;
      if (request.inPersonAddressType === InPersonInterviewAddressType.CompanyAddress)
      {
        request.companyAddressId = vm.companyAddressId;
      } else if (request.inPersonAddressType === InPersonInterviewAddressType.OtherLocation) {
        request.otherLocationId = vm.otherLocationId;
      } else if (request.inPersonAddressType === InPersonInterviewAddressType.OtherAddress) {
        request.buildingNameNumber = vm.buildingNameNumber;
        request.streetRoadBlock = vm.streetRoadBlock;
        request.locality = vm.locality;
        request.cityTown = vm.cityTown;
        request.stateProvinceRegion = vm.stateProvinceRegion;
        request.postZipCode = vm.postZipCode;
        request.countryCode = vm.country.code;
        request.geometry = new GeoPoint();
        request.geometry.lat = vm.geometry.lat;
        request.geometry.lng = vm.geometry.lng;
        request.isExactGeo = true;
      }
    }
    request.workerInstructions = vm.workerInstructions;
    request.contactInstructions = vm.contactInstructions;
    request.sendWorkerConfirmation = vm.sendWorkerConfirmation;
    request.sendContactConfirmation = vm.sendContactConfirmation;

    await ajax.post(`/api/v1/agency/job-workers/${vm.jobWorkerId}/interviews`, request)
    .then(response => {
      this.context.commit("setJobWorkerInterviewFromCreate", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async createJobWorkerOffer(request: CreateJobWorkerOfferRequest) {
    this.setLoadingDialog(true);
    const jobWorkerId = request.jobWorkerId;
    request.jobWorkerId = undefined;
    await ajax.post(`/api/v1/agency/job-workers/${jobWorkerId}/offers`, request)
    .then(response => {
      this.context.commit("setJobWorkerOfferFromCreate", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async editJobWorkerOffer(request: UpdateJobWorkerOfferRequest) {
    this.setLoadingDialog(true);
    const jobWorkerOfferId = request.jobWorkerOfferId;
    request.jobWorkerOfferId = undefined;

    await ajax.put(`/api/v1/agency/job-worker-offers/${jobWorkerOfferId}/details`, request)
    .then(response => {
      this.context.commit("setJobWorkerOfferFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async editJobWorkerPlacement(request: UpdateJobWorkerPlacementRequest) {
    this.setLoadingDialog(true);
    const jobWorkerOfferId = request.jobWorkerOfferId;
    request.jobWorkerOfferId = undefined;

    await ajax.put(`/api/v1/agency/job-worker-offers/${jobWorkerOfferId}/placement`, request)
    .then(response => {
      this.context.commit("setJobWorkerPlacementFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async updateJobWorkerOfferWorkerAcceptance(request: UpdateJobWorkerOfferWorkerAcceptanceRequest) {
    this.setLoadingDialog(true);
    const jobWorkerOfferId = request.jobWorkerOfferId;
    request.jobWorkerOfferId = undefined;

    await ajax.put(`/api/v1/agency/job-worker-offers/${jobWorkerOfferId}/status/worker-acceptance`, request)
    .then(response => {
      this.context.commit("setJobWorkerOfferFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async updateJobWorkerOfferContactConfirmation(request: UpdateJobWorkerOfferContactConfirmationRequest) {
    this.setLoadingDialog(true);
    const jobWorkerOfferId = request.jobWorkerOfferId;
    request.jobWorkerOfferId = undefined;

    await ajax.put(`/api/v1/agency/job-worker-offers/${jobWorkerOfferId}/status/contact-confirmation`, request)
    .then(response => {
      this.context.commit("setJobWorkerOfferFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async sendJobWorkerOfferToWorker(request: SendJobWorkerOfferToWorkerRequest) {
    this.setLoadingDialog(true);
    const jobWorkerOfferId = request.jobWorkerOfferId;
    request.jobWorkerOfferId = undefined;

    await ajax.put(`/api/v1/agency/job-worker-offers/${jobWorkerOfferId}/status/send-to-worker`, request)
    .then(response => {
      this.context.commit("setJobWorkerOfferFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async cancelJobWorkerOffer(request: CancelJobWorkerOfferRequest) {
    this.setLoadingDialog(true);
    const jobWorkerOfferId = request.jobWorkerOfferId;
    request.jobWorkerOfferId = undefined;

    await ajax.put(`/api/v1/agency/job-worker-offers/${jobWorkerOfferId}/status/cancel`, request)
    .then(response => {
      this.context.commit("setJobWorkerOfferFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async placeJobWorkerOffer(request: PlaceJobWorkerOfferRequest) {
    this.setLoadingDialog(true);
    const jobWorkerOfferId = request.jobWorkerOfferId;
    request.jobWorkerOfferId = undefined;

    await ajax.put(`/api/v1/agency/job-worker-offers/${jobWorkerOfferId}/status/place`, request)
    .then(response => {
      this.context.commit("setJobWorkerStatusPlaced", response.data.jobWorkers);
      this.context.commit("setLatestStage", response.data.jobLatestStageStatus);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action 
  async getJobWorkers() {
    this.setLoadingWorkers(true);
    await ajax.get(`/api/v1/agency/jobs/${this.currentJob.id}/workers`)
    .then(response => {
      this.context.commit("setJobWorkers", response.data);
      this.setLoadingWorkers(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingWorkers(false);
    });
  }

  @Action
  public async updateShortlistedSelectedWorker(jobWorker: JobWorkerDto){
    this.context.commit("setShortlistedSelectedWorker", jobWorker);
  }

  @Action
  public async updateInterestedSelectedWorker(jobWorker: JobWorkerDto){
    this.context.commit("setInterestedSelectedWorker", jobWorker);
  }

  @Action
  public async updateRejectedSelectedWorker(jobWorker: JobWorkerDto){
    this.context.commit("setRejectedSelectedWorker", jobWorker);
  }

  @Action
  public async updateJobWorkerStatusWorkerInterested(request: UpdateJobWorkerStatusWorkerInterestedRequest){

    const jobWorkerId = request.jobWorkerId;
    //Remove this as only required to pass into URL
    request.jobWorkerId = undefined;
    await ajax.post(`/api/v1/agency/job-workers/${jobWorkerId}/status/worker-interested`, request)
    .then(response => {
      this.context.commit("setJobWorkerStatusWorkerInterested", response.data.jobWorkers);
      this.context.commit("setLatestStage", response.data.jobLatestStageStatus);
    })
    .catch(error => {
      AppModule.handleApiError(error);
    });
  }

  @Action
  public async updateJobWorkerStatusContactInterested(request: UpdateJobWorkerStatusContactInterestedRequest){

    const jobWorkerId = request.jobWorkerId;
    //Remove this as only required to pass into URL
    request.jobWorkerId = undefined;
    await ajax.post(`/api/v1/agency/job-workers/${jobWorkerId}/status/contact-interested`, request)
    .then(response => {
      this.context.commit("setJobWorkerStatusContactInterested", response.data.jobWorkers);
      this.context.commit("setLatestStage", response.data.jobLatestStageStatus);
    })
    .catch(error => {
      AppModule.handleApiError(error);
    });
  }

  @Action
  public async sendWorkersToContact(request: SendJobWorkersToContactRequest){
    this.setLoadingDialog(true);
    await ajax.post(`/api/v1/agency/jobs/${JobDetailsModule.currentJob.id}/send-to-contact`, request)
    .then(response => {
      this.context.commit("setMoveToSentToContact", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  public async updateJobWorkerStatusInterviewing(request: UpdateJobWorkerStatusInterviewingRequest){

    const jobWorkerId = request.jobWorkerId;
    //Remove this as only required to pass into URL
    request.jobWorkerId = undefined;
    await ajax.post(`/api/v1/agency/job-workers/${jobWorkerId}/status/interviewing`, request)
    .then(response => {
      this.context.commit("setJobWorkerStatusInterviewing", response.data.jobWorkers);
      this.context.commit("setLatestStage", response.data.jobLatestStageStatus);
    })
    .catch(error => {
      AppModule.handleApiError(error);
    });
  }

  @Action
  public async updateJobWorkerStatusOffer(request: UpdateJobWorkerStatusOfferRequest){

    const jobWorkerId = request.jobWorkerId;
    //Remove this as only required to pass into URL
    request.jobWorkerId = undefined;
    await ajax.post(`/api/v1/agency/job-workers/${jobWorkerId}/status/offer`, request)
    .then(response => {
      this.context.commit("setJobWorkerStatusOffer", response.data.jobWorkers);
      this.context.commit("setLatestStage", response.data.jobLatestStageStatus);
    })
    .catch(error => {
      AppModule.handleApiError(error);
    });
  }

  @Action
  public async updateJobWorkerStatusRejected(request: UpdateJobWorkerRejectedRequest){

    const jobWorkerId = request.jobWorkerId;
    //Remove this as only required to pass into URL
    request.jobWorkerId = undefined;
    await ajax.post(`/api/v1/agency/job-workers/${jobWorkerId}/status/rejected`, request)
    .then(response => {
      this.context.commit("setJobWorkerStatusRejected", response.data);
    })
    .catch(error => {
      AppModule.handleApiError(error);
    });
  }


  @Action
  cleanState() {
    this.context.commit("resetState");
  }

  @Mutation
  resetState() {

    this.loading = false;
    this.loadingWorker = false;
    this.loadingWorkers = false;
    this.loadingNotes = true;
    this.loadingDialog = false;
    this.currentJob = new JobDto();
    this.notes = [];
    this.noteCategories = [];
    this.browseWorkersVM = new JobBrowseWorkersViewModel();
    this.workerSearchViewModel = new WorkerSearchViewModel(null, null, null, false);
    this.jobWorkersVM = new JobWorkersViewModel();
  }

  @Mutation
  private async setLoading(isLoading: boolean) {
      this.loading = isLoading;
  }

  @Mutation
  private async setLoadingWorker(isLoading: boolean) {
      this.loadingWorker = isLoading;
  } 

  @Mutation
  private async setLoadingWorkers(isLoading: boolean) {
      this.loadingWorkers = isLoading;
  } 

  @Mutation
  private async setLoadingDialog(isLoading: boolean) {
      this.loadingDialog = isLoading;
  } 

  @Mutation
  private async setNoteCategories(noteCategories: NoteCategoryDto[]) {
      this.noteCategories = noteCategories;
  }

  @Mutation
  private async setNotes(notes: NoteDto[]) {
      this.notes = notes;
  }

  @Mutation
  private async setNoteFromCreate(note: NoteDto) {
      this.notes.unshift(note);
  }

  @Mutation
  private async setNoteFromEdit(note: NoteDto) {
      const index = this.notes.findIndex((findNote: NoteDto) => findNote.id === note.id)
      this.notes.splice(index, 1, note);
  }

  @Mutation
  private async setLoadingNotes(isLoading: boolean) {
      this.loadingNotes = isLoading;
  }

  @Mutation
  private async setCurrentJob(job: JobDto) {
      this.currentJob = job;
      //Also reset the search criteria
      this.browseWorkersVM = new JobBrowseWorkersViewModel();
      this.jobWorkersVM = new JobWorkersViewModel();
  }

  @Mutation
  private async setCurrentJobGeneralDetailsFromEdit(job: JobDto) {
    this.currentJob.updatedAt = job.updatedAt;
    this.currentJob.ownerId = job.ownerId;
    this.currentJob.owner = job.owner;
    this.currentJob.branchId = job.branchId;
    this.currentJob.branch = job.branch;
    this.currentJob.deskId = job.deskId;
    this.currentJob.desk = job.desk;
    this.currentJob.title = job.title;
    this.currentJob.headcount = job.headcount;
    this.currentJob.closingDate = job.closingDate;
    this.currentJob.desiredStartDate = job.desiredStartDate;
    this.currentJob.annualSalaryMin = job.annualSalaryMin;
    this.currentJob.annualSalaryMax = job.annualSalaryMax;
    this.currentJob.feeSource = job.feeSource;
    this.currentJob.purchaseOrderNumber = job.purchaseOrderNumber;
    this.currentJob.isTest = job.isTest;
    this.currentJob.testEmailAddresses = job.testEmailAddresses;
  }

  @Mutation
  private async setCurrentJobLocationDetailsFromEdit(job: JobDto) {
    this.currentJob.updatedAt = job.updatedAt;
    this.currentJob.locationId = job.locationId;
    this.currentJob.location = job.location;
    this.currentJob.contactId = job.contactId;
    this.currentJob.contact = job.contact;
  }

  @Mutation
  private async setCurrentJobRequirementsFromEdit(job: JobDto) {
    this.currentJob.updatedAt = job.updatedAt;
    this.currentJob.primaryPositionId = job.primaryPositionId;
    this.currentJob.primaryPosition = job.primaryPosition;
    this.currentJob.secondaryPositions = job.secondaryPositions;
    this.currentJob.primarySkills = job.primarySkills;
    this.currentJob.primaryQualifications = job.primaryQualifications;
  }

  @Mutation
  private async setCurrentJobStatusFromEdit(job: JobDto) {
    this.currentJob.updatedAt = job.updatedAt;
    this.currentJob.status = job.status;
    this.currentJob.lastClosedReason = job.lastClosedReason;
  }

  @Mutation
  private async setCurrentJobSummaryFromEdit(response: UpdateRichTextResponse) {
    this.currentJob.updatedAt = response.updatedAt;
    this.currentJob.summary = response.richText;
  }

  @Mutation
  private async setCurrentJobDescriptionFromEdit(response: UpdateRichTextResponse) {
    this.currentJob.updatedAt = response.updatedAt
    this.currentJob.description = response.richText;
  }

  @Mutation
  private async setWorkerSearchOptions(options: WorkerSearchOptionsResponse) {
    this.workerSearchViewModel = new WorkerSearchViewModel(options.branches, options.positions,  new DistanceViewModel(20, "20"), false);
  }

  @Mutation
  private async setBrowseJobRequirements() {
    this.browseWorkersVM.selectedWorkerIds = [];
    this.browseWorkersVM = new JobBrowseWorkersViewModel();
    //The position we need is the one from the search options as it has the skills/qualification
    // list populated on it
    const position = this.workerSearchViewModel.positions.find((findPosition: PositionDto) => this.currentJob.primaryPosition.id === findPosition.id);
    this.workerSearchViewModel.selectedPositions = [position];
    //The list of currently selected skills and qualifications need to 
    //come from the booking role
    this.workerSearchViewModel.selectedSkills = this.currentJob.primarySkills.slice();
    this.workerSearchViewModel.selectedQualifications = this.currentJob.primaryQualifications.slice();
    this.workerSearchViewModel.matchSalary = true;
  }

  @Mutation
  private async setBrowseWorker(worker: WorkerProfileDto) {
    if (worker) {
      this.browseWorkersVM.selectedWorkerId = worker.id;
      this.browseWorkersVM.selectedWorker = worker;
    } else {
      this.browseWorkersVM.selectedWorkerId = '';
      this.browseWorkersVM.selectedWorker = null;
    }
    
  }

  @Mutation
  private async setBrowseWorkerProfiles(workerProfiles: WorkerProfileDto[]) {

    this.browseWorkersVM.workerProfiles =  workerProfiles.map(function(workerProfile) {
        return new SelectableEntity<WorkerProfileDto>(workerProfile);
      });
  }

  @Mutation
  private async setAddBrowseSelectedWorkerId(workerId: string) {
    this.browseWorkersVM.selectedWorkerIds.push(workerId);
  }

  @Mutation
  private async setRemoveBrowseSelectedWorkerId(workerId: string) {
    const index = this.browseWorkersVM.selectedWorkerIds.indexOf(workerId);
    this.browseWorkersVM.selectedWorkerIds.splice(index, 1); 
  }

  @Mutation
  private async clearBrowseSelectedWorkerIds() {
    //Clear the id list
    this.browseWorkersVM.selectedWorkerIds = [];
  }

  @Mutation
  private async setJobWorkers(jobWorkers: JobWorkerDto[]) {
    this.jobWorkersVM.jobWorkers = jobWorkers;
    //We also need to update the selected one so that data refreshes as well.
    //TODO: We should really be checking the ID property of the JobWorker but setting the base entity ID to '' causes wider issues
    if (this.jobWorkersVM.selectedShortlistedJobWorker !== null) {
      const selectedJobWorker = jobWorkers.find((findJobWorker: JobWorkerDto) => findJobWorker.id === this.jobWorkersVM.selectedShortlistedJobWorker.id);
      this.jobWorkersVM.selectedShortlistedJobWorker = selectedJobWorker;
    }    
  }

  @Mutation
  private async setJobWorkerInterviewFromCreate(jobWorkerInterview: JobWorkerInterviewDto) {
    const jobWorker = this.jobWorkersVM.jobWorkers.find((jobWorkerToFind: JobWorkerDto) => jobWorkerToFind.id == jobWorkerInterview.jobWorkerId);
    jobWorker.interviews.push(jobWorkerInterview);  
  }

  @Mutation
  private async setJobWorkerInterviewFromEdit(jobWorkerInterview: JobWorkerInterviewDto) {
    const jobWorker = this.jobWorkersVM.jobWorkers.find((jobWorkerToFind: JobWorkerDto) => jobWorkerToFind.id == jobWorkerInterview.jobWorkerId);
    const jobWorkerInterviewIndex = jobWorker.interviews.findIndex((findJobWorkerInterview: JobWorkerInterviewDto) => findJobWorkerInterview.id === jobWorkerInterview.id);
    jobWorker.interviews.splice(jobWorkerInterviewIndex, 1, jobWorkerInterview);  
    this.jobWorkersVM.selectedJobWorkerInterviewForInterviewingTab = jobWorkerInterview;
  }

  @Mutation
  private async setJobWorkerOfferFromCreate(jobWorkerOffer: JobWorkerOfferDto) {
    const jobWorker = this.jobWorkersVM.jobWorkers.find((jobWorkerToFind: JobWorkerDto) => jobWorkerToFind.id == jobWorkerOffer.jobWorkerId);
    jobWorker.offers.push(jobWorkerOffer);  
  }

  @Mutation
  private async setJobWorkerOfferFromEdit(jobWorkerOffer: JobWorkerOfferDto) {
    const jobWorker = this.jobWorkersVM.jobWorkers.find((jobWorkerToFind: JobWorkerDto) => jobWorkerToFind.id == jobWorkerOffer.jobWorkerId);
    const jobWorkerOfferIndex = jobWorker.offers.findIndex((findJobWorkerOffer: JobWorkerOfferDto) => findJobWorkerOffer.id === jobWorkerOffer.id);
    jobWorker.offers.splice(jobWorkerOfferIndex, 1, jobWorkerOffer);  
    this.jobWorkersVM.selectedJobWorkerOfferForOffersTab = jobWorkerOffer;
  }

  @Mutation
  private async setJobWorkerPlacementFromEdit(jobWorkerOffer: JobWorkerOfferDto) {
    const jobWorker = this.jobWorkersVM.jobWorkers.find((jobWorkerToFind: JobWorkerDto) => jobWorkerToFind.id == jobWorkerOffer.jobWorkerId);
    const jobWorkerOfferIndex = jobWorker.offers.findIndex((findJobWorkerOffer: JobWorkerOfferDto) => findJobWorkerOffer.id === jobWorkerOffer.id);
    jobWorker.offers.splice(jobWorkerOfferIndex, 1, jobWorkerOffer);  
    this.jobWorkersVM.selectedJobWorkerOfferForPlacedTab = jobWorkerOffer;
  }

  @Mutation
  private async setJobWorkerStatusWorkerInterested(jobWorkers: JobWorkerDto[]) {
    const jobWorker = jobWorkers[0];
    const jobWorkerToUpdateIndex = this.jobWorkersVM.jobWorkers.findIndex((jobWorkerToFind: JobWorkerDto) => jobWorkerToFind.id == jobWorker.id);
    this.jobWorkersVM.jobWorkers.splice(jobWorkerToUpdateIndex, 1, jobWorker);  
    if (this.jobWorkersVM.currentTabIndex === JobWorkerTabIndex.Shortlisted) {
      this.jobWorkersVM.selectedShortlistedJobWorker = null;
    }
  }

  @Mutation
  private async setMoveToSentToContact(jobWorkers: JobWorkerDto[]) {
    //Update the job workers
    for (let index = 0; index < jobWorkers.length; index++) {
      const jobWorker = jobWorkers[index];
      const jobWorkerToUpdateIndex = this.jobWorkersVM.jobWorkers.findIndex((jobWorkerToFind: JobWorkerDto) => jobWorkerToFind.id == jobWorker.id);
      this.jobWorkersVM.jobWorkers.splice(jobWorkerToUpdateIndex, 1, jobWorker);  
      //Update the selected one if it is one we just sent to contact to refresh it
      if (this.jobWorkersVM.selectedInterestedJobWorker) {
        if (this.jobWorkersVM.selectedInterestedJobWorker.id === jobWorker.id) {
          this.jobWorkersVM.selectedInterestedJobWorker = jobWorker;
        }
      }
    }
  }

  @Mutation
  private async setJobWorkerStatusContactInterested(jobWorkers: JobWorkerDto[]) {
    const jobWorker = jobWorkers[0];
    const jobWorkerToUpdateIndex = this.jobWorkersVM.jobWorkers.findIndex((jobWorkerToFind: JobWorkerDto) => jobWorkerToFind.id == jobWorker.id);
    this.jobWorkersVM.jobWorkers.splice(jobWorkerToUpdateIndex, 1, jobWorker); 
    this.jobWorkersVM.selectedInterestedJobWorker = jobWorker;
    //Movement has occured when status = ContactNotInterested or InterviewOne
    if (jobWorker.status === JobWorkerStatus.ContactNotInterested || jobWorker.status === JobWorkerStatus.InterviewOne) {
      if (this.jobWorkersVM.currentTabIndex === JobWorkerTabIndex.Interested) {
        this.jobWorkersVM.selectedInterestedJobWorker = null;
      }
    }
  }

  @Mutation
  private async setJobWorkerStatusInterviewing(jobWorkers: JobWorkerDto[]) {
    const jobWorker = jobWorkers[0];
    const jobWorkerToUpdateIndex = this.jobWorkersVM.jobWorkers.findIndex((jobWorkerToFind: JobWorkerDto) => jobWorkerToFind.id == jobWorker.id);
    this.jobWorkersVM.jobWorkers.splice(jobWorkerToUpdateIndex, 1, jobWorker);  
    //Movement has occured when status = InterviewOne
    if (jobWorker.status === JobWorkerStatus.InterviewOne) {
      if (this.jobWorkersVM.currentTabIndex === JobWorkerTabIndex.Interested) {
        this.jobWorkersVM.selectedInterestedJobWorker = null;
      }
    } else {
      this.jobWorkersVM.selectedInterviewingJobWorker = jobWorker;
    }
  }

  @Mutation
  private async setJobWorkerStatusOffer(jobWorkers: JobWorkerDto[]) {
    const jobWorker = jobWorkers[0];
    const jobWorkerToUpdateIndex = this.jobWorkersVM.jobWorkers.findIndex((jobWorkerToFind: JobWorkerDto) => jobWorkerToFind.id == jobWorker.id);
    this.jobWorkersVM.jobWorkers.splice(jobWorkerToUpdateIndex, 1, jobWorker);  
    //Movement has come from Interviewing
    if (jobWorker.status === JobWorkerStatus.Offer) {
      if (this.jobWorkersVM.currentTabIndex === JobWorkerTabIndex.Interviewing) {
        this.jobWorkersVM.selectedInterviewingJobWorker = null;
      }
    } 
  }

  @Mutation
  private async setJobWorkerStatusPlaced(jobWorkers: JobWorkerDto[]) {
    const jobWorker = jobWorkers[0];
    const jobWorkerToUpdateIndex = this.jobWorkersVM.jobWorkers.findIndex((jobWorkerToFind: JobWorkerDto) => jobWorkerToFind.id == jobWorker.id);
    this.jobWorkersVM.jobWorkers.splice(jobWorkerToUpdateIndex, 1, jobWorker);  
    //Movement has come from Offer
    if (jobWorker.status === JobWorkerStatus.Placed) {
      if (this.jobWorkersVM.currentTabIndex === JobWorkerTabIndex.Offers) {
        this.jobWorkersVM.selectedOfferJobWorker = null;
      }
    } 
  }

  @Mutation
  private async setJobWorkerStatusRejected(jobWorker: JobWorkerDto) {
    const jobWorkerToUpdateIndex = this.jobWorkersVM.jobWorkers.findIndex((jobWorkerToFind: JobWorkerDto) => jobWorkerToFind.id == jobWorker.id);
    this.jobWorkersVM.jobWorkers.splice(jobWorkerToUpdateIndex, 1, jobWorker);  
    if (this.jobWorkersVM.currentTabIndex === JobWorkerTabIndex.Interviewing) {
      this.jobWorkersVM.selectedInterviewingJobWorker = null;
    } 
    if (this.jobWorkersVM.currentTabIndex === JobWorkerTabIndex.Offers) {
      this.jobWorkersVM.selectedOfferJobWorker = null;
    } 
  }

  @Mutation
  private async setLatestStage(latestStageStatus: JobLatestStageStatusDto) {
    //Update the job stage if required
    if (latestStageStatus.updated) {
      this.currentJob.latestStage = latestStageStatus.jobLatestStage;
      this.currentJob.updatedAt = latestStageStatus.jobUpdatedAt;
      this.currentJob.latestStageAt = latestStageStatus.jobLatestStageAt;
    }
    this.currentJob.workersPlaced = latestStageStatus.jobWorkersPlaced;
  }
}
export const JobDetailsModule = getModule(JobDetails)

